import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Snackbar,
} from '@mui/material';
import { 
  Info as InfoIcon,
  Code as CodeIcon,
  Message as MessageIcon,
  Timer as TimerIcon,
  QrCode as QrCodeIcon,
  FlashOn as FlashOnIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import axios from 'axios';


const StepByStepDirectionsConfig = ({
  config = {},
  onChange,
  showUseParent = false,
  parentOptions = [],
  buttonColor = '#000000',
  disabled = false,
  userId = null,
  entityType = null, // 'Category' or 'SubCategory'
  entityId = null,
  entityName = '',
}) => {
  // Auto-select first parent option if useParent is true and no parent is selected
  useEffect(() => {
    if (showUseParent && config.useParent && parentOptions.length > 0) {
      if (!config.selectedParentId) {
        const firstParent = parentOptions[0];
        onChange({
          ...config,
          selectedParentId: firstParent._id,
          selectedParentType: firstParent.type,
        });
      }
    }
  }, [showUseParent, config.useParent, parentOptions, config.selectedParentId]);

  // Bulk generation state
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);
  
  // Schedule modal state
  const [scheduleModal, setScheduleModal] = useState({ open: false, locationIds: [] });
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Poll for job status
  useEffect(() => {
    if (!bulkJobId || !bulkGenerating) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/wayfinder/step-by-step-directions/bulk-job/${bulkJobId}`);
        
        if (response.data.success) {
          const jobData = response.data.data;
          setBulkJobStatus(jobData);
          setBulkProgress({
            current: jobData.processedLocations,
            total: jobData.totalLocations,
            percent: jobData.progress,
          });

          // Check if job is completed
          if (['completed', 'failed', 'cancelled'].includes(jobData.status)) {
            setBulkGenerating(false);
            clearInterval(pollInterval);

            if (jobData.status === 'completed') {
              // Extract successful location IDs
              const successfulLocationIds = jobData.results
                .filter(r => r.success)
                .map(r => r.locationId);

              if (successfulLocationIds.length > 0) {
                // Auto-fill schedule date/time
                const now = new Date();
                now.setMinutes(now.getMinutes() + 2);
                const dateStr = now.toISOString().split('T')[0];
                const timeStr = now.toTimeString().slice(0, 5);
                setScheduleDate(dateStr);
                setScheduleTime(timeStr);

                // Open schedule modal
                setScheduleModal({
                  open: true,
                  locationIds: successfulLocationIds,
                });

                setSnackbar({
                  open: true,
                  message: `Successfully processed ${jobData.successfulLocations} locations! Text directions have been saved and translated to all available languages.`,
                  severity: 'success',
                });
              } else {
                setSnackbar({
                  open: true,
                  message: 'No locations were successfully processed',
                  severity: 'warning',
                });
              }
            } else if (jobData.status === 'failed') {
              setSnackbar({
                open: true,
                message: `Job failed: ${jobData.errorMessage || 'Unknown error'}`,
                severity: 'error',
              });
            } else if (jobData.status === 'cancelled') {
              setSnackbar({
                open: true,
                message: 'Job was cancelled',
                severity: 'info',
              });
            }
          }
        }
      } catch (error) {
        console.error('[PollJobStatus] Error:', error);
        clearInterval(pollInterval);
        setBulkGenerating(false);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [bulkJobId, bulkGenerating]);

  const handleFieldChange = (field, value) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleNestedFieldChange = (parent, field, value) => {
    onChange({
      ...config,
      [parent]: {
        ...config[parent],
        [field]: value,
      },
    });
  };

  const isDisabled = disabled || (showUseParent && config.useParent);

  // Bulk generate enhanced directions for all locations
  const handleBulkGenerate = async () => {
    if (!userId || !entityType || !entityId) {
      setSnackbar({ open: true, message: 'Missing required information for bulk generation', severity: 'error' });
      return;
    }

    setBulkGenerating(true);
    setBulkProgress({ current: 0, total: 0, percent: 0 });
    setBulkResults(null);
    setBulkJobStatus(null);

    try {
      // Step 1: Get locations for this entity
      setSnackbar({ open: true, message: 'Fetching locations...', severity: 'info' });
      
      const locationsResponse = await axios.get('/api/wayfinder/step-by-step-directions/entity-locations', {
        params: { userId, entityType, entityId }
      });

      if (!locationsResponse.data.success || locationsResponse.data.data.length === 0) {
        setSnackbar({ open: true, message: 'No locations found for this entity', severity: 'warning' });
        setBulkGenerating(false);
        return;
      }

      const locations = locationsResponse.data.data;
      console.log(`[BulkGenerate] Found ${locations.length} locations`);

      // Step 2: Fetch directions from MappedIn (client-side)
      setSnackbar({ open: true, message: `Fetching directions for ${locations.length} locations...`, severity: 'info' });
      
      // Import the bulk directions function
      const { bulkGetDirectionsFromStart } = await import('../../services/mappedInDirectionsService');
      
      // Get MappedIn settings
      const settingsResponse = await axios.get(`/api/wayfinder/mappedin-settings/${userId}`);
      const startLocation = settingsResponse.data.data?.mappedInStartLocation || null;
      const startLocationName = settingsResponse.data.data?.mappedInStartLocationName || null;

      if (!startLocation && !startLocationName) {
        setSnackbar({ open: true, message: 'MappedIn start location not configured', severity: 'error' });
        setBulkGenerating(false);
        return;
      }

      // Fetch all directions at once (client-side bulk fetch)
      const directionsResults = await bulkGetDirectionsFromStart(
        userId,
        startLocation,
        startLocationName,
        locations
      );

      console.log(`[BulkGenerate] Got ${directionsResults.length} direction results`);

      // Step 3: Prepare directions data for backend
      const directionsData = directionsResults
        .filter(result => result.success && result.originalText)
        .map(result => ({
          locationId: result._id,
          locationName: result.name,
          rawDirections: result.originalText || result.text,
        }));

      if (directionsData.length === 0) {
        setSnackbar({ open: true, message: 'Failed to fetch directions for any location', severity: 'error' });
        setBulkGenerating(false);
        return;
      }

      console.log(`[BulkGenerate] Prepared ${directionsData.length} directions for processing`);

      // Step 4: Send to backend for AI enhancement and saving
      setSnackbar({ open: true, message: `Processing ${directionsData.length} locations...`, severity: 'info' });
      
      const response = await axios.post('/api/wayfinder/step-by-step-directions/bulk-generate-enhanced-directions', {
        userId,
        entityType,
        entityId,
        entityName,
        directionsData,
      });

      if (response.data.success) {
        setBulkJobId(response.data.jobId);
        setBulkProgress({
          current: 0,
          total: response.data.totalLocations,
          percent: 0,
        });
        
        setSnackbar({
          open: true,
          message: `Job created! Processing ${response.data.totalLocations} locations in ${response.data.totalChunks} chunks...`,
          severity: 'info',
        });
      }
    } catch (error) {
      console.error('[BulkGenerate] Error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to start bulk generation',
        severity: 'error',
      });
      setBulkGenerating(false);
    }
  };

  // Handle video generation options
  const handleGenerateNow = async () => {
    setScheduleModal(m => ({ ...m, open: false }));
    
    // Trigger video generation for all locations
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const locationId of scheduleModal.locationIds) {
        try {
          // Fetch the location to get its text directions
          const locationResponse = await axios.get(`/api/wayfinder/locations/${locationId}`);
          
          if (locationResponse.data.success) {
            const location = locationResponse.data.data;
            const textDirections = location.textDirectionsMultiLang?.en || location.textDirections || '';

            if (!textDirections || textDirections.trim() === '') {
              console.warn(`[GenerateNow] Location ${locationId} has no text directions, skipping`);
              errorCount++;
              continue;
            }

            // Use force-regenerate endpoint to always generate new videos
            await axios.put(`/api/wayfinder/locations/${locationId}/force-regenerate-video`, {
              textDirections: textDirections,
              currentLanguage: 'en',
            });

            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`[GenerateNow] Error for location ${locationId}:`, error);
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        setSnackbar({
          open: true,
          message: `Video generation started for ${successCount} location(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
          severity: errorCount > 0 ? 'warning' : 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to start video generation for any location',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('[GenerateNow] Error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to start video generation',
        severity: 'error',
      });
    }
  };

  const handleScheduleLater = async () => {
    if (!scheduleDate || !scheduleTime) {
      setSnackbar({ open: true, message: 'Please select date and time', severity: 'error' });
      return;
    }

    setIsScheduling(true);
    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const locationId of scheduleModal.locationIds) {
        try {
          // Fetch the location to get its text directions
          const locationResponse = await axios.get(`/api/wayfinder/locations/${locationId}`);
          
          if (locationResponse.data.success) {
            const location = locationResponse.data.data;
            const textDirections = location.textDirectionsMultiLang?.en || location.textDirections || '';

            if (!textDirections || textDirections.trim() === '') {
              console.warn(`[ScheduleLater] Location ${locationId} has no text directions, skipping`);
              errorCount++;
              continue;
            }

            await axios.post(`/api/wayfinder/locations/${locationId}/schedule-video-generation`, {
              textDirections: textDirections,
              language: 'en',
              scheduledDateTime,
            });

            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`[ScheduleLater] Error for location ${locationId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setSnackbar({
          open: true,
          message: `Video generation scheduled for ${successCount} location(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
          severity: errorCount > 0 ? 'warning' : 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to schedule video generation for any location',
          severity: 'error',
        });
      }
      
      setScheduleModal(m => ({ ...m, open: false }));
      setScheduleDate('');
      setScheduleTime('');
    } catch (error) {
      console.error('[ScheduleLater] Error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to schedule video generation',
        severity: 'error',
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const handleSaveScriptOnly = () => {
    setScheduleModal(m => ({ ...m, open: false }));
    setSnackbar({
      open: true,
      message: `Text directions saved and translated for ${scheduleModal.locationIds.length} locations. Go to "All Locations" tab and refresh to see the pending video state.`,
      severity: 'success',
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Bulk Generation Button - Only show for Category/SubCategory */}
      {userId && entityType && entityId && (
        <Card sx={{ mb: 3, boxShadow: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon />
                  Bulk Text Generation
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Generate AI-enhanced directions for all locations under this {entityType.toLowerCase()}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={bulkGenerating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <FlashOnIcon />}
                onClick={handleBulkGenerate}
                disabled={bulkGenerating}
                sx={{
                  bgcolor: '#fff',
                  color: '#667eea',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255,255,255,0.5)',
                    color: 'rgba(102,126,234,0.5)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {bulkGenerating ? 'Generating...' : 'Generate All Directions'}
              </Button>
            </Box>
            {bulkGenerating && bulkProgress.total > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                    Processing: {bulkProgress.current} / {bulkProgress.total}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                    {bulkProgress.percent}%
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', height: 8, bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 4, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${bulkProgress.percent}%`,
                      height: '100%',
                      bgcolor: '#fff',
                      transition: 'width 0.3s',
                      boxShadow: '0 0 10px rgba(255,255,255,0.5)',
                    }}
                  />
                </Box>
                {bulkJobStatus && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1, display: 'block' }}>
                    Chunk {bulkJobStatus.currentChunk} of {bulkJobStatus.totalChunks} • 
                    Success: {bulkJobStatus.successfulLocations} • 
                    Failed: {bulkJobStatus.failedLocations}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Use Parent Options */}
      {showUseParent && (
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.useParent || false}
                  onChange={(e) => handleFieldChange('useParent', e.target.checked)}
                  sx={{
                    color: buttonColor,
                    '&.Mui-checked': { color: buttonColor },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Use Parent Category/SubCategory Configuration
                  </Typography>
                  <InfoIcon fontSize="small" color="action" />
                </Box>
              }
            />

            {config.useParent && parentOptions.length > 0 && (
              <Box sx={{ mt: 3, ml: 4, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 2, color: buttonColor, fontWeight: 600 }}>
                    Select Parent Configuration
                  </FormLabel>
                  <RadioGroup
                    value={config.selectedParentId || ''}
                    onChange={(e) => {
                      const selectedOption = parentOptions.find(opt => opt._id === e.target.value);
                      onChange({
                        ...config,
                        selectedParentId: e.target.value,
                        selectedParentType: selectedOption?.type || null,
                      });
                    }}
                  >
                    {parentOptions.map((parent, index) => (
                      <Paper
                        key={parent._id}
                        elevation={config.selectedParentId === parent._id ? 3 : 1}
                        sx={{
                          p: 2,
                          mb: 1.5,
                          border: config.selectedParentId === parent._id ? `2px solid ${buttonColor}` : '1px solid #e0e0e0',
                          backgroundColor: config.selectedParentId === parent._id ? `${buttonColor}08` : 'white',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 3,
                            borderColor: buttonColor,
                          },
                        }}
                      >
                        <FormControlLabel
                          value={parent._id}
                          control={
                            <Radio
                              sx={{
                                color: buttonColor,
                                '&.Mui-checked': { color: buttonColor },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography fontWeight={600}>{parent.name}</Typography>
                              <Chip
                                label={parent.type}
                                size="small"
                                sx={{
                                  backgroundColor: buttonColor,
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              />
                              {parent.mapTimeDelay !== null && parent.mapTimeDelay !== undefined && (
                                <Chip
                                  icon={<TimerIcon sx={{ fontSize: '0.9rem' }} />}
                                  label={`${parent.mapTimeDelay}s delay`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              )}
                              {index === 0 && !config.selectedParentId && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            {config.useParent && parentOptions.length === 0 && (
              <Alert severity="info" sx={{ mt: 2, ml: 4 }}>
                No parent categories or subcategories available
              </Alert>
            )}

            {config.useParent && (
              <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
                When using parent configuration, the fields below are inherited and cannot be edited here. 
                Only the AI-enhanced directions will be customized for this location.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Fields */}
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
         

          <Grid container spacing={3}>
            {/* Code Insert 1 */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CodeIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Code Insert 1
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.codeInsert1 || ''}
                  onChange={(e) => handleFieldChange('codeInsert1', e.target.value)}
                  placeholder='<break time="1.5s" />'
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  SSML code to insert before opening text
                </Typography>
              </Box>
            </Grid>

            {/* Opening */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f0f7ff', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MessageIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Opening Message
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={config.opening || ''}
                  onChange={(e) => handleFieldChange('opening', e.target.value)}
                  placeholder="Let me help you find your destination."
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Initial greeting text (supports {'<Location Name>'} placeholder)
                </Typography>
              </Box>
            </Grid>

            {/* Map Time Delay */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#fff8e1', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TimerIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Map Time Delay
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={config.mapTimeDelay !== null && config.mapTimeDelay !== undefined ? config.mapTimeDelay : ''}
                  onChange={(e) => handleFieldChange('mapTimeDelay', e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="0"
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                  slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Delay before showing map (seconds)
                </Typography>
              </Box>
            </Grid>

            {/* Code Insert 2 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CodeIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Code Insert 2
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.codeInsert2 || ''}
                  onChange={(e) => handleFieldChange('codeInsert2', e.target.value)}
                  placeholder='<break time="1.0s" />'
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  After opening, before directions
                </Typography>
              </Box>
            </Grid>

            {/* Step by Step Opening */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f0f7ff', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MessageIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Step-by-Step Opening
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={config.stepByStepOpening || ''}
                  onChange={(e) => handleFieldChange('stepByStepOpening', e.target.value)}
                  placeholder="To get to the <Location Name> first turn <Right / Left>"
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Introduction to directions (use {'<Location Name>'} placeholder)
                </Typography>
              </Box>
            </Grid>

            {/* Step by Step Directions */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#e8f5e9', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MessageIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Step-by-Step Directions
                  </Typography>
                  <Chip label="AI Enhanced" size="small" color="success" />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={config.stepByStepDirections || ''}
                  onChange={(e) => handleFieldChange('stepByStepDirections', e.target.value)}
                  placeholder="<Directions from map>"
                  disabled={disabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Actual turn-by-turn directions (auto-filled by AI enhancement). This field remains editable even when using parent configuration.
                </Typography>
              </Box>
            </Grid>

            {/* Code Insert 3 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CodeIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Code Insert 3
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.codeInsert3 || ''}
                  onChange={(e) => handleFieldChange('codeInsert3', e.target.value)}
                  placeholder='<break time="0.5s" />'
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  After directions, before closing
                </Typography>
              </Box>
            </Grid>

            {/* Closing */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, backgroundColor: '#f0f7ff', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MessageIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Closing Message
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.closing || ''}
                  onChange={(e) => handleFieldChange('closing', e.target.value)}
                  placeholder="Have a nice day."
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Final farewell message
                </Typography>
              </Box>
            </Grid>

            {/* Code Insert 4 */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CodeIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Code Insert 4
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.codeInsert4 || ''}
                  onChange={(e) => handleFieldChange('codeInsert4', e.target.value)}
                  placeholder='<break time="0.5s" />'
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  After closing, before QR instruction
                </Typography>
              </Box>
            </Grid>

            {/* Global QR Closing */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#fce4ec', borderRadius: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.globalQrClosing?.enabled || false}
                      onChange={(e) => handleNestedFieldChange('globalQrClosing', 'enabled', e.target.checked)}
                      disabled={isDisabled}
                      sx={{
                        color: buttonColor,
                        '&.Mui-checked': { color: buttonColor },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QrCodeIcon sx={{ color: buttonColor }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Enable QR Code Instruction
                      </Typography>
                    </Box>
                  }
                />

                {config.globalQrClosing?.enabled && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={config.globalQrClosing?.text || ''}
                    onChange={(e) => handleNestedFieldChange('globalQrClosing', 'text', e.target.value)}
                    placeholder="Scan the QR Code to take these directions with you."
                    disabled={isDisabled}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  QR code scanning instruction for users
                </Typography>
              </Box>
            </Grid>

            {/* Code Insert 5 */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CodeIcon sx={{ color: buttonColor }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Code Insert 5 (Final)
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={config.codeInsert5 || ''}
                  onChange={(e) => handleFieldChange('codeInsert5', e.target.value)}
                  placeholder='<break time="3.5s" />'
                  disabled={isDisabled}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Final SSML code at the end
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ═══ Schedule / Generate / Cancel Modal ═══ */}
      <Dialog
        open={scheduleModal.open}
        onClose={() => setScheduleModal(m => ({ ...m, open: false }))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.3)' } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          color: '#f8fafc', textAlign: 'center', py: 3, px: 4, position: 'relative'
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.3px', mb: 0.5, color: "#fff" }}>
            Video Generation Options
          </Typography>
          <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: 13, background: 'rgba(255,255,255,0.1)', padding: '2px 10px', borderRadius: 20 }}>
            {scheduleModal.locationIds.length} location(s) ready
          </Typography>
          <IconButton
            onClick={() => setScheduleModal(m => ({ ...m, open: false }))}
            sx={{ position: 'absolute', right: 16, top: 16, color: '#94a3b8', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3.5, pb: 1, px: 4, bgcolor: '#f8fafc' }} style={{ paddingTop: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Generate Now */}
            <Button
              variant="contained"
              fullWidth
              startIcon={<FlashOnIcon sx={{ fontSize: 22 }} />}
              onClick={handleGenerateNow}
              sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', textTransform: 'none', fontWeight: 800, borderRadius: '14px', py: 1.8,
                fontSize: 16, color: '#fff', boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(16, 185, 129, 0.4)' },
              }}
            >
              Generate Now
            </Button>

            {/* Schedule Option */}
            <Paper
              elevation={0}
              sx={{
                p: 3, border: '2px solid #e2e8f0', borderRadius: '16px', background: '#ffffff',
                transition: 'all 0.3s ease',
                '&:hover': { borderColor: '#93c5fd', background: '#f0f9ff', boxShadow: '0 8px 24px rgba(59, 130, 246, 0.08)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ p: 1, borderRadius: '10px', background: '#e0f2fe', color: '#0284c7' }}>
                  <ScheduleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Schedule for Later</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Date</Typography>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" sx={{ fontWeight: 700, color: '#64748b', mb: 0.5, display: 'block', lineHeight: 1 }}>Time</Typography>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 10, border: '2px solid #cbd5e1',
                      fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      color: '#334155', background: '#f8fafc',
                      transition: 'border 0.2s'
                    }}
                    onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                    onBlur={(e) => e.target.style.border = '2px solid #cbd5e1'}
                  />
                </Box>
              </Box>
              <Button
                variant="contained"
                fullWidth
                onClick={handleScheduleLater}
                disabled={isScheduling || !scheduleDate || !scheduleTime}
                startIcon={isScheduling ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ScheduleIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', textTransform: 'none', fontWeight: 700, borderRadius: '10px', py: 1.2,
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                  '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
                  '&:disabled': { background: '#cbd5e1', color: '#f8fafc' },
                }}
              >
                {isScheduling ? 'Scheduling…' : 'Confirm Schedule'}
              </Button>
            </Paper>

            {/* Cancel (Save Script Only) */}
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button
                variant="text"
                startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                onClick={handleSaveScriptOnly}
                sx={{
                  color: '#64748b', textTransform: 'none', fontWeight: 600, fontSize: 14, px: 3, py: 1, borderRadius: 8,
                  '&:hover': { color: '#0f172a', background: '#f1f5f9' },
                }}
              >
                Just save script, skip video for now
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, bgcolor: '#f8fafc' }}>
          {/* Empty DialogActions for spacing */}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StepByStepDirectionsConfig;


